/**
 * Turn a JourneyTrigger into a single-sentence English description.
 * Used on the Journey Detail page so operators don't have to mentally
 * translate `{type: 'event', eventName: 'first_checkin_completed'}` into
 * "fires when users complete their first check-in."
 */

import type { JourneyDefinition } from '../types';

type Trigger = JourneyDefinition['entryTrigger'];

export function describeTrigger(trigger: Trigger): string {
  switch (trigger.type) {
    case 'event':
      return trigger.eventName
        ? `Fires on event \`${trigger.eventName}\``
        : 'Fires on an event (no eventName configured)';
    case 'condition': {
      const c = trigger.condition;
      if (!c) return 'Fires when a user condition is met (no condition set)';
      return `Fires when \`${c.field}\` ${describeOperator(c.operator)} ${describeValue(c.value)}`;
    }
    case 'scheduled': {
      const cadence = trigger.cronExpression
        ? humanizeCron(trigger.cronExpression)
        : 'on a schedule';
      return `Runs on schedule: ${cadence}`;
    }
    default:
      return 'Unknown trigger';
  }
}

function describeOperator(op: string): string {
  switch (op) {
    case 'eq':
      return '==';
    case 'gt':
      return '>';
    case 'lt':
      return '<';
    case 'gte':
      return '>=';
    case 'lte':
      return '<=';
    case 'exists':
      return 'exists';
    default:
      return op;
  }
}

function describeValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return `"${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/**
 * Tiny cron humanizer covering the patterns this codebase uses. Falls
 * back to the raw expression for anything exotic. Kept local so we
 * don't pull in `cronstrue` (~5kb) for three patterns.
 */
export function humanizeCron(expr: string): string {
  const trimmed = expr.trim();
  const byPattern: Record<string, string> = {
    '*/5 * * * *': 'every 5 min',
    '*/10 * * * *': 'every 10 min',
    '*/15 * * * *': 'every 15 min',
    '*/30 * * * *': 'every 30 min',
    '0 * * * *': 'every hour on the hour',
    '0 0 * * *': 'daily at 00:00 UTC',
    '0 12 * * *': 'daily at 12:00 UTC',
  };
  return byPattern[trimmed] ?? `cron \`${trimmed}\``;
}
